// Client-side error reporting. Best-effort, deduped and throttled.
// Never throws, never blocks the UI. Errors land in Firestore `systemLogs`
// (via the logClientError Cloud Function) and show in the Admin panel.
// Tambem espelha pro Sentry, que cuida de alerta em tempo real.

import { captureSentryError } from './sentry';

const ENDPOINT = '/api/logClientError';

// Ruído injetado pelo próprio in-app browser do Instagram/Facebook (script de
// telemetria deles, não nosso) — dispara ao sair da página quando a ponte
// nativa (iOS webkit.messageHandlers / Android iabjs bridge) já foi destruída.
// Não é um bug do site: acontece em qualquer site aberto pelo IAB do Meta.
const IGNORED_MESSAGE_PATTERNS = [
  /webkit\.messageHandlers/i,
  /iabjs:\/\//i,
  /sendDataToNative/i,
  /Java object is gone/i,
  // Validação de tamanho de upload (UploadTooLargeError) — a pessoa já vê
  // o toast explicando o limite e pode escolher outro arquivo. Não é um bug
  // pra investigar, então não deve gerar alerta de alta prioridade.
  /^Arquivo muito grande \(/,
];

function isIgnoredNoise({ message, stack, url }) {
  // O script do IAB do Meta às vezes quebra com "Unexpected end of input" sem
  // stack nem mensagem reconhecível — só o `url` (iabjs://...) entrega a origem.
  const combined = `${message || ''} ${stack || ''} ${url || ''}`;
  return IGNORED_MESSAGE_PATTERNS.some((re) => re.test(combined));
}

const recent = new Map(); // signature -> last sent ms
const DEDUPE_MS = 30000;
let sentInWindow = 0;
let windowStart = Date.now();
const MAX_PER_MINUTE = 20;

function throttled() {
  const now = Date.now();
  if (now - windowStart > 60000) {
    windowStart = now;
    sentInWindow = 0;
  }
  if (sentInWindow >= MAX_PER_MINUTE) return true;
  sentInWindow += 1;
  return false;
}

export function reportClientError({ message, stack = '', url = '', level = 'error', context = '', weddingId = '' }) {
  try {
    const msg = String(message || '').slice(0, 1000);
    if (!msg) return;
    if (isIgnoredNoise({ message: msg, stack, url })) return;
    const sig = `${level}|${msg}|${context}`;
    const now = Date.now();
    const last = recent.get(sig);
    if (last && now - last < DEDUPE_MS) return;
    recent.set(sig, now);
    if (recent.size > 200) recent.clear();
    if (throttled()) return;

    const payload = JSON.stringify({
      message: msg,
      stack: String(stack || '').slice(0, 4000),
      url: String(url || (typeof window !== 'undefined' ? window.location.href : '')).slice(0, 500),
      level,
      context: String(context || '').slice(0, 200),
      weddingId: String(weddingId || '').slice(0, 128),
    });

    // Espelha pro Sentry — alerta em tempo real (no-op se DSN ausente).
    captureSentryError({ message: msg, stack, context, level, extra: { weddingId } });

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* observability must never break the app */
  }
}

let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) => {
    reportClientError({
      message: e?.message || 'window.onerror',
      stack: e?.error?.stack || '',
      url: e?.filename || window.location.href,
      context: 'window.error',
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    reportClientError({
      message: (reason && (reason.message || String(reason))) || 'unhandledrejection',
      stack: reason?.stack || '',
      context: 'unhandledrejection',
    });
  });
}
